import React from 'react';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import Fab from '@material-ui/core/Fab';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import { roundPaymentPerMonth } from '../../../utils/price';
import cn from './PaymentPlanCard.module.scss';
import { Radio } from "@material-ui/core";

export class PaymentPlanCard extends React.PureComponent {
  state = {
    showCreateOptionInput: false,
    createOptionMonth: '',
    createOptionFee: '',
    price: '',
    error: '',
    defaultPlan: false,
  };
  priceRef = React.createRef();

  componentDidMount() {
    const { paymentPlan: { price, default: defaultPlan }, error } = this.props;
    this.setState({
      price,
      error,
      defaultPlan,
    });

    if(price === 0) {
      this.priceRef.current.focus();
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { paymentPlan: { price, default: defaultPlan }, error } = this.props;
    if (prevProps.paymentPlan.price !== price || prevProps.error !== error || prevProps.paymentPlan.default !== defaultPlan) {
      this.setState({
        price,
        error,
        defaultPlan,
      });
    }
  }

  handlePriceChange = () => {
    const { onPaymentPlanChange, paymentPlan } = this.props;
    const { price } = this.state;
    onPaymentPlanChange(paymentPlan, {
      ...paymentPlan,
      price,
    });
  };

  handleInputChange = (event) => {
    const value = parseInt(event.target.value);
    this.setState({
      [event.target.name]: isNaN(value) ? 0 : value,
    });
  };

  handlePaymentOptionChange = (option, checked) => {
    const { onPaymentPlanChange, paymentPlan } = this.props;
    onPaymentPlanChange(paymentPlan, {
      ...paymentPlan,
      options: paymentPlan.options
        .map(paymentOption => paymentOption === option ? { ...option, enabled: checked } : paymentOption),
    });
  };

  handleShowCreatePaymentOption = () => {
    this.setState(state => ({
      showCreateOptionInput: !state.showCreateOptionInput,
      createOptionMonth: '',
      createOptionFee: '',
    }));
  };

  isAddPaymentPlanButtonDisabled = () => {
    const { paymentPlan } = this.props;
    const { createOptionMonth } = this.state;

    return !createOptionMonth
      || createOptionMonth < 2
      || paymentPlan.options.find(option => option.months === createOptionMonth);
  };

  handleAddPaymentOption = () => {
    const { onPaymentPlanChange, paymentPlan } = this.props;
    const { createOptionMonth, createOptionFee } = this.state;
    onPaymentPlanChange(paymentPlan, {
      ...paymentPlan,
      options: [
        ...paymentPlan.options,
        { months: createOptionMonth, fee: createOptionFee, enabled: true },
      ],
    });
    this.setState({
      showCreateOptionInput: false,
      createOptionMonth: '',
      createOptionFee: '',
    })
  };

  handleRemovePaymentOption = (option) => {
    const { onPaymentPlanChange, paymentPlan } = this.props;
    onPaymentPlanChange(paymentPlan, {
      ...paymentPlan,
      options: paymentPlan.options.filter(paymentOption => paymentOption !== option),
    });
  };

  render() {
    const { paymentPlan, onPaymentPlanDelete, onPaymentPlanDefaultChange, className } = this.props;
    const { options } = paymentPlan;
    const { createOptionMonth, showCreateOptionInput, createOptionFee, price, error, defaultPlan } = this.state;
    return (
      <Card className={className} data-type="payment-card">
        <CardContent className={cn.paymentPlanCard}>
          <IconButton onClick={() => onPaymentPlanDelete(paymentPlan)} className={cn.deleteButton}>
            <CloseIcon />
          </IconButton>
          <FormControlLabel
            control={
              <Radio
                checked={defaultPlan}
                onClick={() => onPaymentPlanDefaultChange(paymentPlan)}
                color="primary"
              />
            }
            label="Set as default payment plan"
            classes={{ root: cn.defaultPlanLabel}}
          />
          <div className={cn.label}>Set application price</div>
          <FormGroup row classes={{root: cn.applicationPriceInput }}>
            <TextField
              label="Application price"
              name="price"
              value={price}
              onChange={this.handleInputChange}
              onBlur={this.handlePriceChange}
              error={!!error}
              helperText={error}
              margin="dense"
              inputRef={this.priceRef}
              InputProps={{
                endAdornment: '$'
              }}
            />
          </FormGroup>
          <FormGroup>
            <div className={cn.configurePaymentPlans}>
              <div className={cn.sectionHeader}>Configure payment plans</div>
              <Fab
                onClick={this.handleShowCreatePaymentOption}
                color="primary"
                size="small"
              >
                {showCreateOptionInput ? <CloseIcon/> : <AddIcon/>}
              </Fab>
            </div>
            {options.map(option => (
              <div className={cn.paymentOption} key={option.months} data-type="payment-option" data-enabled={option.enabled} data-months={option.months} data-fee={option.fee || 0}>
                <FormControlLabel
                  control={
                    <Checkbox
                      value={option.months}
                      color="primary"
                      disabled={option.months === 1}
                      checked={option.enabled}
                      onChange={(event, checked) => this.handlePaymentOptionChange(option, checked)}
                    />
                  }
                  label={option.months === 1 ? `One time ($${price} with no fee)` : `${option.months} month ($${
                    roundPaymentPerMonth(price, option.months)}/month, $${option.fee || 0} fee)`
                  }
                />
                {option.months !== 1 && (
                  <IconButton onClick={() => this.handleRemovePaymentOption(option)} size="small">
                    <DeleteIcon />
                  </IconButton>
                )}
              </div>
            ))}
            {showCreateOptionInput && (
              <div className={cn.addPaymentOption}>
                <div className={cn.addPaymentOptionInputsRow}>
                  <TextField
                    label="Months"
                    name="createOptionMonth"
                    value={createOptionMonth}
                    onChange={this.handleInputChange}
                    classes={{ root: cn.textField }}
                  />
                  <TextField
                    label="Per month"
                    value={createOptionMonth > 0 ? roundPaymentPerMonth(price, createOptionMonth) : ''}
                    disabled
                    InputProps={{
                      endAdornment: '$'
                    }}
                    classes={{ root: cn.textField }}
                  />
                  <TextField
                    label="Fee"
                    name="createOptionFee"
                    value={createOptionFee}
                    onChange={this.handleInputChange}
                    classes={{ root: cn.textField }}
                  />
                </div>
                <div className={cn.addPaymentOptionButton}>
                  <Button
                    onClick={this.handleAddPaymentOption}
                    color="primary"
                    variant="contained"
                    disabled={this.isAddPaymentPlanButtonDisabled()}
                  >Add</Button>
                </div>
              </div>
            )}
          </FormGroup>
        </CardContent>
      </Card>
    );
  }
}
